import {
    CommandType,
    handleMessage as nodeHandleMessage,
    buildDomains,
    InternalType,
    MessageType,
    GatewayNode,
    NodeMessage,
    NodeOptions,
    RequestType,
    SendMessageAction
} from '../node.js';
import {
    isClusterAddress,
    isNodeAddress,
    isPeerAddress,
    NodeAddress,
    PeerId,
    Source
} from '../state/types/common.js';
import { ClusterType, Mesh, MeshMessageAction, MeshMessageBodyType } from '../mesh.js';
import {HandleReturnType, Domain} from '../domain.js';
import {failure} from '../constants.js';
import {emptyState} from '../state/core.js';
import {getLogger} from '../logger.js';
import {error} from '../common/messages.js';
import {exceptionToReason} from '../reason.js';
import {friendlily, some} from '../common/utilities.js';
import {MessageReceiver} from '../common/channel.js';
import {byId} from '../state/peers.js';
import {State} from '../state/types/state.js';
import {IOGateway} from '../../gateway';

type MeshNodeOptions = NodeOptions

class MeshNode implements GatewayNode {
    constructor(private readonly handler: MessageReceiver<NodeMessage<RequestType | CommandType>>,
                private readonly cluster: Mesh,
                private readonly nodeId: string) {
    }

    close(): void {
        this.cluster.unregister(this.nodeId);
        this.cluster.close();
    }

    message(msg: NodeMessage<RequestType>) {
        this.handler(msg);
    }

    addSource(source: Source) {
        // do nothing
    }

    removeSource(source: Source): void {
        const msg: NodeMessage<CommandType> = {
            origin: 'local',
            source,
            body: {type: 'commands/source-removed'}
        }
        return this.handler(msg);
    }
}

const log = getLogger('gateway.node.mesh');

type MeshNodeMessage<T> = NodeMessage<T> & { receiver: Source }
type ClusterNodeMessage = MeshNodeMessage<CommandType | ClusterType> & { origin: 'cluster' };

function handleMessage(state: State, msg: MeshNodeMessage<MeshMessageBodyType> | NodeMessage<RequestType | CommandType | InternalType>): HandleReturnType {
    const {source, body, origin/*, receiver*/} = msg;
    // const nodeId = state.ids.nodeId;
    try {
        if (body['dump']) {
            log.info(`state dump:\n${JSON.stringify(friendlily(state), null, '\t')}`);
            return [state, []];
        } else if (origin === 'cluster') {
            return handleClusterMessage(state, msg as ClusterNodeMessage);
        } else {
            return nodeHandleMessage(state, msg as NodeMessage<RequestType | CommandType | InternalType>, source);
        }
    } catch (e) {
        log.error(`Error handling message ${JSON.stringify(msg)}`, e);
        const m = body as {
            request_id?: string
            peer_id?: PeerId
        };
        return [state, [error(undefined, source, m.request_id, m.peer_id, exceptionToReason(e, failure))]];
    }
}

function sendState(state: State, nodeId: string, targetNodeId: string): HandleReturnType {
    log.info(`Node ${nodeId} is sending state to node ${targetNodeId}`);
    const receiver: NodeAddress = {type: 'node', node: targetNodeId};
    const msgs = Object.values(state.registeredDomains || [])
        .map(value => value.domain)
        .flatMap(domain => domain.stateToMessages(state))
        .filter(some<Omit<SendMessageAction<MessageType>, 'receiver'>>)
        .map<SendMessageAction<MessageType>>(m => {
            return {...m, receiver};
        });
    return [state, msgs];
}

function hasReceiver(address): address is { receive: MessageReceiver<IOGateway.Message<unknown>> } {
    return 'receive' in address && address.receive;
}

function handleClusterMessage(state: State, msg: ClusterNodeMessage): HandleReturnType {
    const nodeId = state.ids.nodeId;
    const {source, body, origin, receiver} = msg;
    if (isPeerAddress(receiver)) {
        // who sets node in the receiver?
        if (nodeId === receiver.node) {
            const peer = byId(state, receiver.peer);
            if (peer) {
                if (hasReceiver(peer.source)) {
                    peer.source.receive(body);
                }
            }
        }
        return [state, []];
    } else if (isNodeAddress(receiver)) {
        if (nodeId === receiver.node) {
            if (body.type === 'send-state') {
                return sendState(state, nodeId, source['node']);
            }
            return nodeHandleMessage(state, msg as NodeMessage<CommandType | RequestType>, source);
        }
        return [state, []];
    } else if (isClusterAddress(receiver)) {
        if (nodeId !== source['node']) {
            if (body.type === 'send-state') {
                return sendState(state, nodeId, source['node']);
            }
            return nodeHandleMessage(state, msg as NodeMessage<CommandType | RequestType>, source);
        }
        return [state, []];
    } else {
        return nodeHandleMessage(state, msg as NodeMessage<CommandType | RequestType>, source);
    }
}

// response processing

function processResponse<T extends MessageType>(nodeId: string, cluster: Mesh, response: MeshMessageAction<T | CommandType>) {
    switch (response.receiver.type) {
        case 'cluster': {
            if (log.enabledFor('debug')) {
                log.debug(`broadcasting message ${JSON.stringify(response)}`);
            }
            cluster.publish(nodeId, response);
            break;
        }
        case 'node': {
            if (log.enabledFor('debug')) {
                log.debug(`unicasting message ${JSON.stringify(response)}`);
            }
            cluster.publish(nodeId, response);
            break;
        }
        case 'peer': {
            if (log.enabledFor('debug')) {
                const {receiver, body} = response;
                if (log.enabledFor('debug')) {
                    log.debug(`Sending message ${JSON.stringify(body, null, '\t')} to remote peer ${JSON.stringify(receiver)}`);
                }
            }
            cluster.publish(nodeId, response);
            break;
        }
        case 'local': {
            const {receiver, body} = response;
            if (log.enabledFor('debug')) {
                log.debug(`Sending message ${JSON.stringify(body, null, '\t')} to local peer ${JSON.stringify(receiver)}`);
            }
            if (hasReceiver(receiver)) {
                receiver.receive(body);
            }
            break;
        }
        default: {
            log.error(`Unable to process response ${JSON.stringify(response)}`);
            break;
        }
    }
}

function localUsers(state: State): ReadonlySet<string> {
    return new Set(state.users?.byName?.keys())
}

function processMessage(state: State, cluster: Mesh, msg: MeshNodeMessage<MeshMessageBodyType> | NodeMessage<InternalType | CommandType | RequestType>) {
    try {
        if (log.enabledFor('trace')) {
            log.debug(`domain handler processing message ${JSON.stringify(msg)}`);
        }
        const nodeId = state.ids.nodeId;
        const [newState, responses] = handleMessage(state, msg);
        const newUsers = localUsers(newState);
        const oldUsers = localUsers(state);
        try {
            for (const m of responses) {

                const x = {...m, source: {...m['source'], node: nodeId}};
                processResponse(nodeId, cluster, x);
            }
        } catch (e) {
            log.error(`error processing responses`, e);
        }
        const added = Array.from(newUsers ?? []).filter((u) => !oldUsers?.has(u));
        if (added && added.length > 0) {
            cluster.addUsers(nodeId, added);
        }
        const removed = Array.from(oldUsers ?? []).filter((u) => !newUsers?.has(u));
        if (removed && removed.length > 0) {
            cluster.removeUsers(nodeId, removed);
        }
        return newState ?? state;
    } catch (e) {
        log.error(`error handling message ${JSON.stringify(msg)}`, e);
        return state;
    }

}

export function meshNode<T extends MessageType = MessageType>(cluster: Mesh, domains: Array<Domain<T>>, options: MeshNodeOptions): GatewayNode {
    let state: State;
    // const ser = new AsyncSerializer();

    const handler = (msg: MeshNodeMessage<MeshMessageBodyType> | NodeMessage<InternalType | CommandType | RequestType>) => {
        state = processMessage(state, cluster, msg);
    };

    const registeredDomains = buildDomains(domains);

    const nodeId = cluster.register(options.nodeId, Object.values(registeredDomains).map(domain => domain.info),
        {
            onMessage: (nodeId: string, msg: MeshMessageAction<MeshMessageBodyType>) => {
                if (log.enabledFor('debug')) {
                    log.debug(`Node ${nodeId} received a message from cluster ${JSON.stringify(msg)}`);
                }
                return handler({...msg, origin: 'cluster'});
            },
            onMemberAdded: (nodeId: string, newNodeId: string) => {
                if (log.enabledFor('debug')) {
                    log.debug(`Node ${nodeId} received a node ${newNodeId} add to cluster.`)
                }
                const receiver: NodeAddress = {type: 'node', node: newNodeId};
                const source: NodeAddress = {type: 'node', node: nodeId};
                const msg: MeshNodeMessage<'send-state'> = {
                    origin: 'cluster',
                    receiver,
                    source,
                    body: {type: 'send-state'}
                };
                return cluster.publish(nodeId, msg);
            },
            onMemberRemoved: (nodeId, removedNodeId) => {
                if (log.enabledFor('debug')) {
                    log.debug(`Node ${nodeId} received a node ${removedNodeId} dropped from cluster`);
                }
                const receiver: NodeAddress = {type: 'node', node: nodeId};
                const source: NodeAddress = {type: 'node', node: removedNodeId};
                const msg: MeshNodeMessage<'commands/source-removed'> = {
                    origin: 'cluster',
                    receiver,
                    source,
                    body: {type: 'commands/source-removed'}
                };
                return handler(msg);
            }
        });

    state = domains.reduce(
        (s: State, domain) => {
            return domain.init(s);
        },
        {
            ...emptyState(nodeId, options.signingKey),
            registeredDomains,
            handler: (msg: NodeMessage<InternalType>) => {
                handler(msg);
            }
        });

    return new MeshNode(handler, cluster, nodeId);
}
